import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/gallery", label: "Gallery" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onHome = location.pathname === "/";
  const onServiceDetail = /^\/services\/[^/]+/.test(location.pathname);
  const transparentHeader = (onHome || onServiceDetail) && !scrolled && !isOpen;
  const lightOnHero = (onHome || onServiceDetail) && !scrolled && !isOpen;
  const invertLogo = onServiceDetail && !scrolled && !isOpen;
  const publicUrl = process.env.PUBLIC_URL || "";
  const logoSrc = onHome && !scrolled
    ? `${publicUrl}/Images/logo2.png`
    : `${publicUrl}/Images/logo.png`;
  let headerClasses =
    "fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ";

  if (isOpen) {
    headerClasses += "bg-aura-bg/95 backdrop-blur-md border-gold-muted/20";
  } else if (transparentHeader) {
    headerClasses += onHome
      ? "bg-transparent border-transparent"
      : "bg-black/40 backdrop-blur-md border-transparent";
  } else {
    headerClasses += "bg-aura-bg/95 backdrop-blur-md border-gold-muted/20";
  }

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={headerClasses}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={logoSrc}
              alt="Aura International"
              className={`h-20 md:h-24 w-auto object-contain ${invertLogo ? "brightness-0 invert" : ""}`}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-sans text-sm font-medium tracking-wide transition-colors duration-300 ${
                  lightOnHero
                    ? location.pathname === link.path
                      ? "text-gold-primary"
                      : "text-white hover:text-gold-primary"
                    : location.pathname === link.path
                    ? "text-gold-primary"
                    : "text-aura-text hover:text-gold-primary"
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.span
                    layoutId="navbar-underline"
                    className="absolute -bottom-1 left-0 right-0 h-px bg-gold-primary"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
            <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg hover:bg-gold-light/30 transition-colors ${lightOnHero ? "text-white" : "text-aura-text"}`}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden border-t border-gold-muted/20 bg-aura-bg/95 backdrop-blur-md"
            >
              <div className="py-4 space-y-1">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`block py-2 px-4 rounded-lg font-sans text-sm font-medium ${
                        location.pathname === link.path
                          ? "text-gold-primary bg-gold-light/20"
                          : "text-aura-text hover:bg-gold-light/20"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
